<script setup lang="ts">
/**
 * DangerZoneTab — settings tab that exposes the two most dangerous AFD
 * RPC operations: manual `saveSession` and `shutdown`. Both go through the
 * authenticated REST surface in `internal/api/tasks_webui.go`.
 *
 * Shutdown uses a hardened confirmation flow (SA-SHUTDOWN-UI):
 *   1. Modal opens with a 5-second countdown before the confirm button arms.
 *   2. The user must type the literal token `SHUTDOWN` into a text input.
 *   3. The body lists concrete side effects so the impact is unambiguous.
 *   4. After the request fires, a 30s watchdog surfaces a timeout toast if
 *      the daemon does not acknowledge in time (the connection will typically
 *      drop on a successful shutdown, so the timeout is the success signal
 *      path on most deployments).
 *
 * Visual treatment follows the existing AuthTab / BTTab pattern (Card +
 * Button + Modal) but every interactive surface is tinted with the danger
 * palette so users clearly see that these are not ordinary settings.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AlertTriangle, Power, Save } from 'lucide-vue-next'
import { useMutation } from '@tanstack/vue-query'
import Card from '@/components/ui/Card.vue'
import Button from '@/components/ui/Button.vue'
import Modal from '@/components/ui/Modal.vue'
import { saveSession, shutdown } from '@/api/rpc'
import { useToast } from '@/composables/useToast'

type ImpactList = string[]

const { t } = useI18n()
const toast = useToast()

const SHOW_SHUTDOWN_DELAY_MS = 5_000
const SHUTDOWN_WATCHDOG_MS = 30_000
/** Token the user must type verbatim to arm the shutdown button. */
const SHUTDOWN_CONFIRM_TOKEN = 'SHUTDOWN'

const showShutdownConfirm = ref(false)
const countdown = ref(SHOW_SHUTDOWN_DELAY_MS / 1000)
/** User-typed confirmation token; must equal SHUTDOWN_CONFIRM_TOKEN. */
const confirmText = ref('')
let countdownTimer: ReturnType<typeof setInterval> | null = null
let shutdownWatchdog: ReturnType<typeof setTimeout> | null = null

function clearCountdownTimer(): void {
  if (countdownTimer !== null) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

function clearShutdownWatchdog(): void {
  if (shutdownWatchdog !== null) {
    clearTimeout(shutdownWatchdog)
    shutdownWatchdog = null
  }
}

function startShutdownWatchdog(): void {
  clearShutdownWatchdog()
  shutdownWatchdog = setTimeout(() => {
    // The RPC typically never resolves because the daemon dies — a timeout is
    // the only signal the user gets. Surface it as an explicit toast rather
    // than letting the mutation hang silently.
    toast.warning(t('settings.danger.shutdown.shutdownTimeout'))
    closeShutdownConfirm()
  }, SHUTDOWN_WATCHDOG_MS)
}

const saveMutation = useMutation({
  mutationFn: () => saveSession(),
  onSuccess: () => {
    toast.success(t('settings.danger.saveSession.success'))
  },
  onError: (err) => {
    toast.error(
      t('settings.danger.saveSession.failed') + ': ' + ((err as Error)?.message ?? ''),
    )
  },
})

const shutdownMutation = useMutation({
  mutationFn: () => shutdown(),
  onSuccess: () => {
    clearShutdownWatchdog()
    toast.success(t('settings.danger.shutdown.success'))
    closeShutdownConfirm()
  },
  onError: (err) => {
    clearShutdownWatchdog()
    toast.error(
      t('settings.danger.shutdown.failed') + ': ' + ((err as Error)?.message ?? ''),
    )
  },
})

const isConfirmValid = computed(
  () => confirmText.value.trim() === SHUTDOWN_CONFIRM_TOKEN,
)
const canConfirmShutdown = computed(
  () =>
    countdown.value === 0 &&
    isConfirmValid.value &&
    !shutdownMutation.isPending.value,
)
/** Strongly-typed impact list — keeps vue-tsc happy without `as unknown as`. */
const impactList = computed<ImpactList>(() => {
  const raw = t('settings.danger.shutdown.impactList')
  return Array.isArray(raw) ? (raw as ImpactList) : []
})
/** True while the shutdown RPC is in flight; used to show the spinner state. */
const isShuttingDown = computed(() => shutdownMutation.isPending.value)

function openShutdownConfirm(): void {
  confirmText.value = ''
  countdown.value = SHOW_SHUTDOWN_DELAY_MS / 1000
  showShutdownConfirm.value = true
}

function closeShutdownConfirm(): void {
  clearCountdownTimer()
  clearShutdownWatchdog()
  confirmText.value = ''
  countdown.value = SHOW_SHUTDOWN_DELAY_MS / 1000
  showShutdownConfirm.value = false
}

function askShutdown(): void {
  openShutdownConfirm()
}

function cancelShutdown(): void {
  if (shutdownMutation.isPending.value) return
  closeShutdownConfirm()
}

function confirmShutdown(): void {
  if (!canConfirmShutdown.value) return
  startShutdownWatchdog()
  shutdownMutation.mutate()
}

/**
 * When the modal transitions to open, (re)arm the countdown. When it closes
 * (via backdrop, Escape, cancel, or successful confirm) reset everything so
 * the next opening starts from scratch.
 */
watch(showShutdownConfirm, (open) => {
  if (open) {
    confirmText.value = ''
    countdown.value = SHOW_SHUTDOWN_DELAY_MS / 1000
    clearCountdownTimer()
    countdownTimer = setInterval(() => {
      if (countdown.value > 0) {
        countdown.value -= 1
      } else {
        clearCountdownTimer()
      }
    }, 1000)
  } else {
    clearCountdownTimer()
    clearShutdownWatchdog()
  }
})

onBeforeUnmount(() => {
  clearCountdownTimer()
  clearShutdownWatchdog()
})
</script>

<template>
  <div class="space-y-6">
    <!-- Top-level warning banner -->
    <Card>
      <div class="flex items-start gap-3">
        <div class="rounded-md bg-danger/15 p-2 text-danger">
          <AlertTriangle :size="20" />
        </div>
        <div>
          <h3 class="text-sm font-semibold text-fg">{{ t('settings.danger.title') }}</h3>
          <p class="mt-1 text-xs text-fg-muted">{{ t('settings.danger.subtitle') }}</p>
          <p class="mt-2 text-xs text-danger/90">{{ t('settings.danger.warning') }}</p>
        </div>
      </div>
    </Card>

    <!-- Save session -->
    <Card>
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex items-start gap-3">
          <div class="rounded-md bg-brand-500/15 p-2 text-brand-600 dark:text-brand-300">
            <Save :size="18" />
          </div>
          <div>
            <h3 class="text-sm font-semibold text-fg">{{ t('settings.danger.saveSession.title') }}</h3>
            <p class="mt-1 text-xs text-fg-muted">{{ t('settings.danger.saveSession.subtitle') }}</p>
            <p class="mt-2 text-xs text-fg-subtle">{{ t('settings.danger.saveSession.hint') }}</p>
          </div>
        </div>
        <div class="flex items-center gap-2 sm:flex-col sm:items-stretch">
          <Button
            variant="outline"
            size="md"
            :loading="saveMutation.isPending.value"
            :disabled="shutdownMutation.isPending.value"
            @click="saveMutation.mutate()"
          >
            <template #icon><Save :size="14" /></template>
            {{ t('settings.danger.saveSession.action') }}
          </Button>
        </div>
      </div>
    </Card>

    <!-- Shutdown -->
    <Card class="border-danger/40">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex items-start gap-3">
          <div class="rounded-md bg-danger/15 p-2 text-danger">
            <Power :size="18" />
          </div>
          <div>
            <h3 class="text-sm font-semibold text-fg">{{ t('settings.danger.shutdown.title') }}</h3>
            <p class="mt-1 text-xs text-fg-muted">{{ t('settings.danger.shutdown.subtitle') }}</p>
            <p class="mt-2 text-xs text-fg-subtle">{{ t('settings.danger.shutdown.hint') }}</p>
          </div>
        </div>
        <div class="flex items-center gap-2 sm:flex-col sm:items-stretch">
          <Button
            variant="danger"
            size="md"
            :loading="shutdownMutation.isPending.value"
            :disabled="saveMutation.isPending.value"
            @click="askShutdown"
          >
            <template #icon><Power :size="14" /></template>
            {{ t('settings.danger.shutdown.action') }}
          </Button>
        </div>
      </div>
    </Card>

    <!-- Shutdown confirm modal -->
    <Modal
      :model-value="showShutdownConfirm"
      :title="t('settings.danger.shutdown.confirmTitle')"
      :closable="!isShuttingDown"
      :close-on-backdrop="!isShuttingDown"
      :close-on-escape="!isShuttingDown"
      size="lg"
      @update:modelValue="(v: boolean) => { if (!v) cancelShutdown() }"
    >
      <div class="space-y-4">
        <!-- Prominent red danger banner -->
        <div
          class="flex items-start gap-3 rounded-md border-2 border-danger/60 bg-danger/15 p-4"
          role="alert"
        >
          <AlertTriangle :size="22" class="mt-0.5 shrink-0 text-danger" />
          <div>
            <p class="text-base font-bold uppercase tracking-wide text-danger">
              {{ t('settings.danger.title') }}
            </p>
            <p class="mt-1 text-sm font-medium text-danger">
              {{ t('settings.danger.warning') }}
            </p>
          </div>
        </div>

        <p class="text-sm text-fg">{{ t('settings.danger.shutdown.confirmBody') }}</p>

        <!-- Impact list -->
        <div class="rounded-md border border-danger/30 bg-danger/5 p-3">
          <p class="text-xs font-semibold uppercase tracking-wide text-danger">
            {{ t('settings.danger.shutdown.impactTitle') }}
          </p>
          <ul class="mt-2 space-y-1.5 text-xs text-fg-muted">
            <li
              v-for="(line, idx) in impactList"
              :key="idx"
              class="flex items-start gap-2"
            >
              <span class="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-danger" />
              <span>{{ line }}</span>
            </li>
          </ul>
        </div>

        <!-- Confirm token input -->
        <div class="space-y-1.5">
          <label
            for="shutdown-confirm-input"
            class="block text-xs font-semibold text-fg"
          >
            {{ t('settings.danger.shutdown.confirmInputLabel') }}
          </label>
          <input
            id="shutdown-confirm-input"
            v-model="confirmText"
            type="text"
            autocomplete="off"
            spellcheck="false"
            :placeholder="t('settings.danger.shutdown.confirmInputPlaceholder')"
            :disabled="isShuttingDown"
            class="block w-full rounded-md border border-border bg-bg-elevated px-3 py-2 font-mono text-sm uppercase tracking-wider text-fg placeholder:text-fg-subtle focus:border-danger focus:outline-none focus:ring-2 focus:ring-danger/40 disabled:cursor-not-allowed disabled:opacity-60"
            :class="confirmText.length > 0 && !isConfirmValid ? 'border-danger/60' : ''"
          />
        </div>
      </div>
      <template #footer>
        <Button
          variant="ghost"
          size="sm"
          :disabled="isShuttingDown"
          @click="cancelShutdown"
        >
          {{ t('common.cancel') }}
        </Button>
        <Button
          variant="danger"
          size="sm"
          :loading="isShuttingDown"
          :disabled="!canConfirmShutdown"
          @click="confirmShutdown"
        >
          <template #icon>
            <Power v-if="!isShuttingDown" :size="14" />
          </template>
          {{
            isShuttingDown
              ? t('settings.danger.shutdown.shutdownInProgress')
              : countdown > 0
                ? `${t('settings.danger.shutdown.countdownPrefix')} (${countdown})`
                : t('settings.danger.shutdown.confirmButton')
          }}
        </Button>
      </template>
    </Modal>
  </div>
</template>